import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CallEntity } from './call.entity';
import { UserEntity } from './user.entity';

/**
 * Records the first time a user played a call recording (DD.9.5.3).
 * Powers the persistent "unlistened voicemail" indicator in the
 * Communications Panel — a voicemail counts as "unlistened" by user X
 * when no row exists here for `(fkCallId, fkUserId)`.
 *
 * Per-user (not tenant-wide) so a manager + rep can each have their
 * own unlistened state — mirrors how iOS doesn't clear your phone's
 * voicemail badge when your spouse listens to a voicemail on theirs.
 *
 * **Idempotent**: only the first listen is interesting. The
 * recording-playback endpoint records on every fire; the unique
 * index makes re-fires a no-op without needing per-callsite guards.
 *
 * **Append-only by intent**: no UPDATE or DELETE call sites should
 * exist. If a "mark as unlistened" affordance is added later, it
 * deletes the row rather than mutating it (audit trail intact).
 */
@Index('softphone_call_listen_user_call_uidx', ['fkUserId', 'fkCallId'], {
	unique: true,
})
@Index('softphone_call_listen_user_idx', ['fkUserId'])
@Entity('softphone_call_listen_event')
export class SoftphoneCallListenEventEntity extends BaseEntity {
	@Column('int', { name: 'fk_call__id', unsigned: true })
	public fkCallId!: number;

	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/**
	 * Timestamp of the first playback. Distinct from
	 * BaseEntity.createdDate (which is also set on insert) so a
	 * future "mark as unlistened then re-listen" flow can update
	 * this without losing the original `createdDate` audit trail.
	 */
	@Column('datetime', {
		name: 'listenedAt',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public listenedAt!: Date;

	@ManyToOne(() => CallEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_call__id', referencedColumnName: 'id' }])
	public fkCall!: CallEntity;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
